import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

type Language = 'ko' | 'en'

interface LanguageContextType {
    language: Language
    setLanguage: (lang: Language) => void
    t: (key: string) => any
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
    children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        if (typeof window === 'undefined') return 'ko'
        const saved = localStorage.getItem('language')
        if (saved === 'ko' || saved === 'en') {
            return saved as Language
        }
        const browserLang = navigator.language || navigator.languages?.[0] || 'en'
        if (browserLang.toLowerCase().startsWith('ko')) {
            return 'ko'
        }
        
        // 기본값은 영어
        return 'en'
    })

    useEffect(() => {
        localStorage.setItem('language', language)
    }, [language])

    const setLanguage = (lang: Language) => {
        setLanguageState(lang)
    }

    // 번역 함수
    const t = (key: string): any => {
        const keys = key.split('.')
        let value: any = translations[language]

        for (const k of keys) {
            if (value && typeof value === 'object') {
                value = value[k]
            } else {
                return key // 키를 찾지 못하면 키 자체 반환
            }
        }

        // 문자열, 배열, 객체 모두 반환 가능
        return value !== undefined ? value : key
    }

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    )
}

export const useLanguage = () => {
    const context = useContext(LanguageContext)
    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider')
    }
    return context
}

// 번역 데이터
const translations = {
    ko: {
        nav: {
            home: '처음으로',
            story: '1931의 이야기',
            storyPage: '이야기',
            location: '오시는 길',
            specialty: '특별한 맛',
            menu: '메뉴 보기',
            gallery: '갤러리',
            reviews: '후기',
            press: '언론보도',
            contact: '예약·문의',
            darkMode: '다크 모드',
            lightMode: '라이트 모드'
        },
        hero: {
            title: 'BLACK PORK',
            subtitle: '1931',
            description: '정통 흑돼지의 깊은 맛과 향',
            explore: '자세히 보기'
        },
        intro: {
            label: 'About Us',
            title: '정성과 전통이 만들어낸 최고의 맛',
            imageAlt: '레스토랑 주인',
            quote: "'삼겹살의 처음 그 맛을 기억하시나요?'",
            quoteAuthor: '- 흑돈1931 김민중 대표',
            sections: {
                promise: {
                    title: '1931년의 약속',
                    content: '1931년 \'세겹살\'이라는 이름으로 등장한 흑돼지 삼겹살. 그 순간의 원형을 복원하고 싶었습니다. 그래서 흑돈1931이라는 이름을 지었습니다. 1931년의 흑돼지 삼겹살을 지금 이 자리에서 다시 맛보게 하겠다는 약속을 담았습니다.'
                },
                philosophy: {
                    title: '전문성과 철학',
                    content: '외식 컨설턴트와 숙성전문가, 축산물위생교육원 강사로 일하며 다양한 브랜드를 경험했습니다. 고기는 과학이자 철학이라는 사실을 깊이 깨달았고, 직접 농장에서 국내 최고 품종인 버크셔K 흑돼지를 공수해 숙성하고 정형하자고 결심했습니다.'
                },
                aging: {
                    title: '160시간 이상의 숙성',
                    content: '흑돈1931의 모든 고기는 160시간 이상 정성껏 숙성합니다. 숙성은 단순히 시간 싸움이 아닙니다. 고기의 결, 지방의 흐름, 온도와 습도를 모두 고려한 섬세한 조율입니다. 그래서 많은 손님들이 고기가 왜 이렇게 부드럽고 감칠맛이 깊은지 물어보십니다.'
                },
                menu: {
                    title: '대표 메뉴',
                    items: {
                        samgyeopsal: {
                            name: '버크셔K 삼겹살',
                            desc: '입에 넣는 순간 지방과 살코기가 부드럽게 어우러져 얇지 않아도 부담스럽지 않은 맛'
                        },
                        moksal: {
                            name: '우리흑돈 목살',
                            desc: '대구 덕유농장에서 공수한 고기로 고소한 풍미와 탱탱한 조직감이 매력'
                        },
                        galbi: {
                            name: '돼지갈비',
                            desc: '퍽퍽하지 않으면서도 절제된 단맛이 인상 깊은 메뉴'
                        },
                        kimchi: {
                            name: '김치찌개',
                            desc: '식사의 마무리는 늘 진한 김치찌개로 고기와 가장 잘 어울리는 조합'
                        }
                    }
                },
                operation: {
                    title: '운영 안내',
                    content: '점심 시간에는 커피농장의 하루의 원두로 내린 아메리카노를 무료로 제공합니다. 좋은 고기를 먹고 좋은 커피로 마무리하는 소박하지만 만족스러운 구성입니다.\n\n점심은 가락시장 직장인들의 가성비 맛집으로, 저녁은 회식과 가족모임의 단골로 자리잡았습니다. 14인까지 이용 가능한 프라이빗 룸은 단체 회식, 기념일 모임에 적합하며, 웨이팅 전용 공간도 마련되어 있어 기다림마저 편안하게 만들고 싶었습니다.'
                },
                recognition: {
                    title: '인정받은 품질',
                    content: '외식 전문지 \'외식경영\'의 강사로 활동하며 숙달돼지 육대장 등 다양한 브랜드 프로젝트에도 참여 중입니다. 최근에는 서울특별시의회 의장님으로부터 지역사회 공헌 표창도 수여 받았습니다. 유튜브 맥형TV, 돼지배움터, 외식경영 등에서도 흑돈1931의 이야기를 소개해 주셨습니다.'
                }
            },
            highlights: {
                aging: {
                    title: '160시간 이상 숙성',
                    desc: '정성껏 숙성한 버크셔K 흑돼지'
                },
                representative: {
                    title: '대표 메뉴',
                    desc: '버크셔K 삼겹살, 우리흑돈 목살'
                },
                lunch: {
                    title: '점심 특별 서비스',
                    desc: '커피농장 원두 아메리카노 무료 제공'
                },
                room: {
                    title: '프라이빗 룸',
                    desc: '14인까지 이용 가능한 단체 공간'
                }
            },
            closing: '흑돈1931은 트렌드를 좇기보단 진짜 맛의 본질에 집중합니다.\n삼겹살이 처음 세겹살이라 불리던 그 시절처럼 다시 한 번 고기의 원형을 경험하고 싶은 분들이 찾아주시길 바랍니다.\n\n삼겹살의 처음 맛을 지금 이 자리에서 다시 느껴보세요.\n바로 아시게 될겁니다. 흑돈1931의 고기맛의 차이를...'
        },
        menu: {
            label: 'Our Menu',
            title: '특별한 메뉴',
            description: '정성스럽게 준비한 블랙포크1931의 시그니처 메뉴를 만나보세요',
            viewDetail: '자세히 보기',
            showcase: {
                item1: {
                    title: '프리미엄 흑돼지 세트',
                    description: '최상급 흑돼지 삼겹살과 목살을 한 번에 즐길 수 있는 시그니처 메뉴입니다. 신선한 고기의 육즙과 고소한 풍미가 일품입니다.',
                    price: '45,000원'
                },
                item2: {
                    title: '특선 흑돼지 구이',
                    description: '90년 전통의 비법으로 숙성시킨 흑돼지를 직화로 구워내어 깊은 맛과 향을 자랑합니다. 부드러운 식감이 일품인 대표 메뉴입니다.',
                    price: '38,000원'
                },
                item3: {
                    title: '명품 흑돼지 모듬',
                    description: '다양한 부위를 맛볼 수 있는 모듬 구성으로, 흑돼지 본연의 맛을 제대로 느낄 수 있습니다. 특제 소스와 함께 제공됩니다.',
                    price: '52,000원'
                },
                item4: {
                    title: '정통 흑돼지 코스',
                    description: '전채부터 메인, 식사까지 풀코스로 즐기는 프리미엄 메뉴입니다. 블랙포크1931의 진수를 경험하실 수 있습니다.',
                    price: '68,000원'
                }
            },
            detailTitle: '메뉴 구성',
            detailLabel: 'Menu',
            categories: {
                lunch: '점심특선',
                set: '세트 메뉴',
                main: '대표 메뉴',
                other: '기타 메뉴'
            },
            badge: '대표',
            items: {
                lunch: [
                    {
                        name: '[점심특선] 한돈인증 흑돈 김치찌개',
                        description: '흑돈1931엔 한돈인증 김치찌게 1만원에 커피까지 드립니다. 2인이사부터 한돈김치찌게~',
                        price: '10,000원'
                    },
                    {
                        name: '[점심특선] 우삼겹 된장전골',
                        description: '미나리가 들어간 된장전골에 우삼겹이 듬뿍~!',
                        price: '10,000원'
                    },
                    {
                        name: '[점심특선] 한돈인증 흑돼지 주물럭',
                        description: '먹어보면 멈출 수 없는 젓가락질~! 흑돼지의 고소함과 매콤 달달한 육즙이 입안에 퍼집니다.',
                        price: '10,000원'
                    }
                ],
                set: [
                    {
                        name: '4인세트 (추가 메뉴중 선택2)',
                        description: '버크셔K 삼겹살과 우리흑돈 목살 그리고 흑돼지 앞다리로 이루어진 세트메뉴',
                        price: '90,000원'
                    },
                    {
                        name: '3인세트 (추가 메뉴중 선택2)',
                        description: '버크셔K 삼겹살과 우리흑돈 목살 그리고 흑돼지 앞다리로 이루어진 세트메뉴',
                        price: '67,000원'
                    },
                    {
                        name: '2인세트 (추가 메뉴중 선택1)',
                        description: '버크셔K 삼겹살과 우리흑돈 목살 그리고 흑돼지 앞다리로 이루어진 세트메뉴',
                        price: '45,000원'
                    }
                ],
                main: [
                    {
                        name: '남원 흑돼지 삼겹살 (160g)',
                        description: '버크셔K 흑돼지의 삼겹살입니다. 가락시장 40년경력 정육점사장님이 마감후 드시는 숙성삼겹살',
                        price: '17,000원'
                    },
                    {
                        name: '대구 우리흑돈 목살 (160g)',
                        description: '대구 우리흑돈의 목살입니다. 경찰병원 간호사 분들이 극찬한 가락동 대표 1등 메뉴입니다.',
                        price: '17,000원'
                    },
                    {
                        name: '흑돈소금구이 (200g)',
                        description: '꾸리살, 부체살, 앞다리살 모듬구이로 구성된 가락시장 상인들이 제일 좋아하는 메뉴입니다',
                        price: '17,000원'
                    },
                    {
                        name: '한돈목살양념구이',
                        description: '한돈인증점이기에 수입산이 아닌 한돈목살로 만든 돼지양념구이 가격 그리고 양 품질 최고!!',
                        price: '17,000원'
                    }
                ],
                other: [
                    {
                        name: '미・술밥',
                        price: '7,000원'
                    },
                    {
                        name: '추가 흑돼지 김치찌개',
                        price: '9,000원'
                    },
                    {
                        name: '계란찜',
                        price: '3,000원'
                    },
                    {
                        name: '물냉면',
                        price: '8,000원'
                    }
                ]
            }
        },
        gallery: {
            label: 'Gallery',
            title: '갤러리',
            description: '1931년부터 이어온 정통의 맛, 흑돈1931의 특별한 순간들',
            morePhotos: '더 많은 사진 보기'
        },
        reviews: {
            label: 'Reviews',
            title: '리뷰',
            description: '고객님들이 직접 경험한 흑돈1931의 진짜 이야기',
            moreReviews: '더 많은 리뷰 보기',
            items: [
                {
                    name: '김*호',
                    comment: '160시간 숙성 삼겹살이 정말 부드럽고 맛있어요. 프라이빗 룸도 좋아서 가족 모임하기 딱이에요!',
                    date: '2024.11.15'
                },
                {
                    name: '이*진',
                    comment: '버크셔K 흑돼지 정말 최고입니다. 직원분들도 친절하시고 점심특선 가성비 좋네요!',
                    date: '2024.11.10'
                },
                {
                    name: '박*수',
                    comment: '송파구에서 가장 맛있는 흑돼지집이에요. 주차도 편하고 분위기도 좋습니다.',
                    date: '2024.11.08'
                },
                {
                    name: '최*영',
                    comment: '회식으로 다녀왔는데 14인 룸이 정말 좋았습니다. 무료 커피까지 완벽해요.',
                    date: '2024.11.05'
                }
            ]
        },
        footer: {
            brand: 'BLACKPORK',
            brandNumber: '1931',
            address: '서울 송파구 송파대로30길 41-26 1층',
            addressDetail: '가락시장역 3번 출구에서 680m',
            seatingTitle: '좌석·공간',
            seatingRoom: '룸',
            seatingRoomValue: '최소 10명 ~ 최대 12명',
            seatingGroup: '단체석',
            seatingGroupValue: '최소 30명 ~ 최대 32명',
            seatingStanding: '입식',
            naverInfo: '네이버 정보',
            viewMap: '지도 보기',
            contact: '연락처',
            phone: '전화',
            email: '이메일',
            location: '위치',
            locationValue: '서울 송파구 송파대로30길 41-26 1층 가락시장역 3번 출구에서 680m',
            hours: '영업시간',
            hoursValue: '월-일 11:00 - 22:00',
            parkingTitle: '주차',
            parkingAvailable: '주차가능',
            parkingFree: '무료',
            parkingNotice: '매장 주차장이 협소하여 미리연락 부탁드립니다.\n만차일 경우 주변주차장을 이용해주십시요.',
            parkingLot1: '아마노 가락본동 주차장',
            parkingLot1Info: '30분 3천원\n60분 6천원\n모두의 주차장 쿠폰구매시 3시간 1만원',
            parkingLot2: '경찰병원 주차장',
            parkingLot2Info: '최초 30분 무료\n주간 오전6시~오후10시 10분당 5백원\n야간 오후10시~오전6시 10분당 1천원',
            facilitiesTitle: '편의시설 및 서비스',
            corkagePaid: '콜키지 가능',
            corkagePaidBadge: '유료',
            corkageFree: '콜키지 가능',
            corkageFreeBadge: '무료',
            groupAvailable: '단체 이용 가능',
            reservation: '예약',
            babyChair: '유아의자',
            waitingArea: '대기공간',
            wifi: '무선 인터넷',
            restroom: '남/녀 화장실 구분',
            wheelchair: '출입구 휠체어 이용가능',
            inquiryTitle: '문의하기',
            emailPlaceholder: '이메일 주소',
            messagePlaceholder: '메시지를 입력하세요',
            sending: '전송 중...',
            sent: '전송 완료!',
            sendMessage: '메시지 보내기',
            copyright: '© 2024 BLACKPORK1931. All rights reserved.'
        },
        cta: {
            phone: '전화',
            phoneFull: '전화 걸기',
            phoneAria: '흑돈1931 전화 걸기',
            reserve: '예약',
            reserveFull: '예약하기',
            reserveAria: '네이버에서 흑돈1931 예약하기',
            viewMenu: '메뉴 보러가기',
            directions: '길찾기',
            directionsAria: '흑돈1931 길찾기',
            reviews: '리뷰',
            reviewsAria: '흑돈1931 방문자 리뷰 보기',
            googleMapsDirections: 'Google Maps 길찾기',
            naverReviews: '방문자 리뷰 확인하기',
            instagramVisits: '인스타그램 방문 사진 보기'
        },
        localIntent: {
            label: 'GARAK MARKET BLACK PORK',
            ariaTitle: '지역 정보',
            title: '가락시장 흑돼지 맛집을 찾는다면 흑돈1931',
            description: '서울 송파구 가락시장 인근에서 160시간 숙성 흑돼지 삼겹살, 우리흑돈 목살, 점심특선과 프라이빗 룸을 함께 찾는 분들을 위한 고기집입니다.',
            locationLabel: '위치',
            locationValue: '서울 송파구 송파대로30길 41-26 1층',
            menuLabel: '대표 메뉴',
            menuValue: '숙성 흑돼지 삼겹살, 우리흑돈 목살, 특선 세트',
            parkingLabel: '주차',
            parkingValue: '건물 주차 가능, 만차 시 가락본동·경찰병원 주차장 이용',
            phoneAction: '전화 문의',
            reserveAction: '네이버 예약',
            directionsAction: '길찾기'
        }
    },
    en: {
        nav: {
            home: 'Home',
            story: 'Our Story',
            storyPage: 'Story',
            location: 'Location',
            specialty: 'Specialty',
            menu: 'Menu',
            gallery: 'Gallery',
            reviews: 'Reviews',
            press: 'Press',
            contact: 'Contact',
            darkMode: 'Dark Mode',
            lightMode: 'Light Mode'
        },
        hero: {
            title: 'BLACK PORK',
            subtitle: '1931',
            description: 'Authentic Black Pork Heritage',
            explore: 'Explore More'
        },
        intro: {
            label: 'About Us',
            title: 'The Finest Taste Created by Dedication and Tradition',
            imageAlt: 'Restaurant Owner',
            quote: "'Do you remember the first taste of samgyeopsal?'",
            quoteAuthor: '- Kim Min-jung, CEO of BlackPork1931',
            sections: {
                promise: {
                    title: 'The Promise of 1931',
                    content: 'Black pork samgyeopsal that emerged in 1931 under the name "Segyeopsal". We wanted to restore the original form of that moment. That\'s why we named it BlackPork1931. It embodies our promise to bring back the taste of 1931\'s black pork samgyeopsal here and now.'
                },
                philosophy: {
                    title: 'Expertise and Philosophy',
                    content: 'Working as a restaurant consultant, aging specialist, and instructor at the Livestock Hygiene Education Center, I experienced various brands. I deeply realized that meat is both science and philosophy, and decided to source Berkshire K black pork, the finest domestic breed, directly from farms for aging and processing.'
                },
                aging: {
                    title: 'Aging for Over 160 Hours',
                    content: 'All meat at BlackPork1931 is carefully aged for over 160 hours. Aging is not just about time. It\'s a delicate adjustment considering meat grain, fat distribution, temperature, and humidity. That\'s why many customers ask why the meat is so tender with such deep umami flavor.'
                },
                menu: {
                    title: 'Signature Menu',
                    items: {
                        samgyeopsal: {
                            name: 'Berkshire K Samgyeopsal',
                            desc: 'The moment it touches your palate, fat and lean meat blend smoothly for a non-burdensome taste even with thick cuts'
                        },
                        moksal: {
                            name: 'Korean Black Pork Neck',
                            desc: 'Sourced from Daegu Deokyoo Farm, featuring savory flavor and firm texture'
                        },
                        galbi: {
                            name: 'Pork Ribs',
                            desc: 'Memorable menu with restrained sweetness without being dry'
                        },
                        kimchi: {
                            name: 'Kimchi Stew',
                            desc: 'The perfect combination with meat, finishing meals with rich kimchi stew'
                        }
                    }
                },
                operation: {
                    title: 'Operating Information',
                    content: 'During lunch hours, we offer complimentary Americano made with Haru coffee beans from Coffee Farm. A simple yet satisfying composition of great meat finished with good coffee.\n\nLunch has become a value dining spot for Garak Market workers, while dinner is a regular choice for company dinners and family gatherings. The private room for up to 14 people is perfect for group dinners and special occasions, with a dedicated waiting area to make even the wait comfortable.'
                },
                recognition: {
                    title: 'Recognized Quality',
                    content: 'Active as an instructor for the restaurant magazine "Restaurant Management" and participating in various brand projects including Sukdal Pork. Recently received a commendation for community contribution from the Seoul Metropolitan Council Chairman. BlackPork1931\'s story has also been featured on YouTube channels like Mac Hyung TV, Pork Learning Center, and Restaurant Management.'
                }
            },
            highlights: {
                aging: {
                    title: 'Aged 160+ Hours',
                    desc: 'Carefully aged Berkshire K black pork'
                },
                representative: {
                    title: 'Signature Menu',
                    desc: 'Berkshire K Samgyeopsal, Korean Black Pork Neck'
                },
                lunch: {
                    title: 'Lunch Special Service',
                    desc: 'Complimentary Coffee Farm Americano'
                },
                room: {
                    title: 'Private Room',
                    desc: 'Group space for up to 14 people'
                }
            },
            closing: 'BlackPork1931 focuses on the essence of real taste rather than chasing trends.\nWe hope those who want to experience the original form of meat will visit us, just like when samgyeopsal was first called segyeopsal.\n\nFeel the original taste of samgyeopsal here and now.\nYou will immediately notice the difference in BlackPork1931\'s meat quality...'
        },
        menu: {
            label: 'Our Menu',
            title: 'Special Menu',
            description: 'Discover BlackPork1931\'s carefully prepared signature dishes',
            viewDetail: 'View Details',
            showcase: {
                item1: {
                    title: 'Premium Black Pork Set',
                    description: 'Our signature menu featuring the finest black pork belly and neck in one set. Enjoy the fresh meat\'s juice and savory flavor.',
                    price: '₩45,000'
                },
                item2: {
                    title: 'Special Black Pork Grill',
                    description: 'Black pork aged with our 90-year traditional method, grilled over charcoal for deep taste and aroma. Our signature dish with tender texture.',
                    price: '₩38,000'
                },
                item3: {
                    title: 'Premium Black Pork Assortment',
                    description: 'An assortment of various cuts to truly taste the authentic flavor of black pork. Served with our special sauce.',
                    price: '₩52,000'
                },
                item4: {
                    title: 'Traditional Black Pork Course',
                    description: 'A premium full course from appetizers to main and meal. Experience the essence of BlackPork1931.',
                    price: '₩68,000'
                }
            },
            detailTitle: 'Menu Composition',
            detailLabel: 'Menu',
            categories: {
                lunch: 'Lunch Special',
                set: 'Set Menu',
                main: 'Signature Menu',
                other: 'Other Menu'
            },
            badge: 'Signature',
            items: {
                lunch: [
                    {
                        name: '[Lunch Special] Certified Korean Black Pork Kimchi Stew',
                        description: 'At BlackPork1931, enjoy certified Korean black pork kimchi stew for ₩10,000 with complimentary coffee. Available for 2+ people.',
                        price: '₩10,000'
                    },
                    {
                        name: '[Lunch Special] Beef Brisket Soybean Paste Hot Pot',
                        description: 'Soybean paste hot pot with water parsley and plenty of beef brisket!',
                        price: '₩10,000'
                    },
                    {
                        name: '[Lunch Special] Certified Korean Black Pork Jumulleok',
                        description: 'You can\'t stop eating once you start! Savory black pork with sweet and spicy juices spreading in your mouth.',
                        price: '₩10,000'
                    }
                ],
                set: [
                    {
                        name: '4-Person Set (Choose 2 additional items)',
                        description: 'Set menu featuring Berkshire K samgyeopsal, Korean black pork neck, and black pork shoulder',
                        price: '₩90,000'
                    },
                    {
                        name: '3-Person Set (Choose 2 additional items)',
                        description: 'Set menu featuring Berkshire K samgyeopsal, Korean black pork neck, and black pork shoulder',
                        price: '₩67,000'
                    },
                    {
                        name: '2-Person Set (Choose 1 additional item)',
                        description: 'Set menu featuring Berkshire K samgyeopsal, Korean black pork neck, and black pork shoulder',
                        price: '₩45,000'
                    }
                ],
                main: [
                    {
                        name: 'Namwon Black Pork Samgyeopsal (160g)',
                        description: 'Berkshire K black pork belly. The aged samgyeopsal enjoyed by a butcher with 40 years of experience at Garak Market after closing.',
                        price: '₩17,000'
                    },
                    {
                        name: 'Daegu Korean Black Pork Neck (160g)',
                        description: 'Korean black pork neck from Daegu. The #1 menu in Garak-dong praised by nurses at Police Hospital.',
                        price: '₩17,000'
                    },
                    {
                        name: 'Black Pork Salt Grill (200g)',
                        description: 'Assorted grill of shoulder blade, fan, and shoulder. The favorite menu of Garak Market merchants.',
                        price: '₩17,000'
                    },
                    {
                        name: 'Korean Pork Neck Seasoned Grill',
                        description: 'As a certified Korean pork restaurant, made with domestic Korean pork neck, not imported. Best price, quantity, and quality!!',
                        price: '₩17,000'
                    }
                ],
                other: [
                    {
                        name: 'Rice with Soy Sauce',
                        price: '₩7,000'
                    },
                    {
                        name: 'Additional Black Pork Kimchi Stew',
                        price: '₩9,000'
                    },
                    {
                        name: 'Steamed Egg',
                        price: '₩3,000'
                    },
                    {
                        name: 'Cold Noodles',
                        price: '₩8,000'
                    }
                ]
            }
        },
        gallery: {
            label: 'Gallery',
            title: 'Gallery',
            description: 'Special moments at BlackPork1931, continuing the authentic taste since 1931',
            morePhotos: 'More Photos'
        },
        reviews: {
            label: 'Reviews',
            title: 'Reviews',
            description: 'Real stories from customers who experienced BlackPork1931',
            moreReviews: 'More Reviews',
            items: [
                {
                    name: 'Kim**',
                    comment: 'The 160-hour aged samgyeopsal is incredibly tender and delicious. The private room is perfect for family gatherings!',
                    date: '2024.11.15'
                },
                {
                    name: 'Lee**',
                    comment: 'Berkshire K black pork is absolutely amazing. Staff are friendly and the lunch special offers great value!',
                    date: '2024.11.10'
                },
                {
                    name: 'Park**',
                    comment: 'Best black pork restaurant in Songpa-gu. Convenient parking and great atmosphere.',
                    date: '2024.11.08'
                },
                {
                    name: 'Choi**',
                    comment: 'Had a company dinner here. The 14-person private room was excellent. Free coffee is a perfect touch!',
                    date: '2024.11.05'
                }
            ]
        },
        footer: {
            brand: 'BLACKPORK',
            brandNumber: '1931',
            address: '41-26, Songpadae-ro 30-gil, Songpa-gu, Seoul, 1F',
            addressDetail: '680m from Garak Market Station Exit 3',
            seatingTitle: 'Seating & Space',
            seatingRoom: 'Private Room',
            seatingRoomValue: 'Min 10 ~ Max 12 people',
            seatingGroup: 'Group Seating',
            seatingGroupValue: 'Min 30 ~ Max 32 people',
            seatingStanding: 'Standing',
            naverInfo: 'Naver Info',
            viewMap: 'View Map',
            contact: 'Contact',
            phone: 'Phone',
            email: 'Email',
            location: 'Location',
            locationValue: '41-26, Songpadae-ro 30-gil, Songpa-gu, Seoul, 1F (680m from Garak Market Station Exit 3)',
            hours: 'Business Hours',
            hoursValue: 'Mon-Sun 11:00 - 22:00',
            parkingTitle: 'Parking',
            parkingAvailable: 'Available',
            parkingFree: 'Free',
            parkingNotice: 'Our parking lot is limited, please contact us in advance.\nIf full, please use nearby parking lots.',
            parkingLot1: 'Amano Garak Bondong Parking',
            parkingLot1Info: '30min ₩3,000\n60min ₩6,000\n3hrs ₩10,000 with coupon',
            parkingLot2: 'Police Hospital Parking',
            parkingLot2Info: 'First 30min free\nDay 6AM-10PM ₩500/10min\nNight 10PM-6AM ₩1,000/10min',
            facilitiesTitle: 'Facilities & Services',
            corkagePaid: 'Corkage Available',
            corkagePaidBadge: 'Paid',
            corkageFree: 'Corkage Available',
            corkageFreeBadge: 'Free',
            groupAvailable: 'Group Available',
            reservation: 'Reservation',
            babyChair: 'Baby Chair',
            waitingArea: 'Waiting Area',
            wifi: 'WiFi',
            restroom: 'Separate Restrooms',
            wheelchair: 'Wheelchair Accessible',
            inquiryTitle: 'Contact Us',
            emailPlaceholder: 'Email Address',
            messagePlaceholder: 'Enter your message',
            sending: 'Sending...',
            sent: 'Sent!',
            sendMessage: 'Send Message',
            copyright: '© 2024 BLACKPORK1931. All rights reserved.'
        },
        cta: {
            phone: 'Call',
            phoneFull: 'Call Us',
            phoneAria: 'Call BlackPork1931',
            reserve: 'Reserve',
            reserveFull: 'Make a Reservation',
            reserveAria: 'Reserve BlackPork1931 on Naver',
            viewMenu: 'View the Menu',
            directions: 'Directions',
            directionsAria: 'Get directions to BlackPork1931',
            reviews: 'Reviews',
            reviewsAria: 'View BlackPork1931 visitor reviews',
            googleMapsDirections: 'Get directions on Google Maps',
            naverReviews: 'Read visitor reviews',
            instagramVisits: 'See Instagram visit photos'
        },
        localIntent: {
            label: 'GARAK MARKET BLACK PORK',
            ariaTitle: 'Local information',
            title: 'Looking for the best black pork near Garak Market? BlackPork1931.',
            description: 'A black pork specialty restaurant near Garak Market in Songpa-gu, Seoul, offering 160-hour aged Berkshire K samgyeopsal, Korean black pork neck, lunch specials, and a private room.',
            locationLabel: 'Location',
            locationValue: '1F, 41-26 Songpa-daero 30-gil, Songpa-gu, Seoul',
            menuLabel: 'Signature Menu',
            menuValue: 'Aged samgyeopsal, Korean black pork neck, signature set',
            parkingLabel: 'Parking',
            parkingValue: 'On-site parking; nearby Garakbon-dong and Police Hospital lots when full',
            phoneAction: 'Call',
            reserveAction: 'Reserve on Naver',
            directionsAction: 'Directions'
        }
    }
}
